import { create } from 'zustand';
import type {
  ClientData,
  GateFields,
  InsertFile,
  Signatures,
  SigningPersonId,
  GateSigningPersonId,
  Variant,
  WindowsFields,
  WizardState,
  WizardStep,
} from '../types/wizard.types';
import { MAX_INSERTS } from '../types/wizard.types';
import { formatGateTotal, GATE_BREAKDOWN_KEYS } from '../utils/gateAmounts';
import {
  formatKpTotal,
  formatUnitedTotal,
  KP_BREAKDOWN_KEYS,
  PODRYAD_BREAKDOWN_KEYS,
  UNITED_BREAKDOWN_KEYS,
} from '../utils/windowsAmounts';

const initialWindows: WindowsFields = {
  contractNumber: '',
  contractDate: new Date().toISOString().slice(0, 10),
  totalAmount: '',
  constructionsAmount: '',
  garbageRemovalAmount: '',
  slopesAmount: '',
  deliveryAmount: '',
  prepaymentAmount: '',
  prepaymentDueDate: '',
  paymentMethod: 'cash',
  installationAddress: '',
  measurementDone: false,
  sizesBySeller: false,
  podryadPrepaymentMode: 'half',
  podryadPrepaymentAmount: '',
  workDemontage: '',
  workInstallation: '',
  workSlopes: '',
  workFlashings: '',
  workAdditional: '',
};

const initialGate: GateFields = {
  contractNumber: '',
  contractDate: new Date().toISOString().slice(0, 10),
  paymentType: '50-50',
  totalAmount: '',
  prepaymentAmount: '',
  prepaymentDueDate: '',
  paymentMethod: 'cash',
  installationAddress: '',
  measurementDone: false,
  sizesBySeller: false,
  productAmount: '',
  preparationMaterial: '',
  deliveryAmount: '',
  installationAmount: '',
  preparationWork: '',
  demontageAmount: '',
};

const initialClient: ClientData = {
  fullName: '',
  passportSeries: '',
  passportNumber: '',
  passportIssuedBy: '',
  passportIssuedDate: '',
  registeredAddress: '',
  actualAddress: '',
  phone: '',
  phoneSecondary: '',
  email: '',
};

const initialSignatures: Signatures = {
  employee: null,
  employeePodryad: null,
  windowsKp: null,
  windowsPodryad: null,
  gate: null,
};

interface WizardStoreApi extends WizardState {
  step: WizardStep;
  goTo: (step: WizardStep) => void;
  setVariant: (v: Variant) => void;
  setSigningPersonId: (id: SigningPersonId) => void;
  setGateSigningPersonId: (id: GateSigningPersonId) => void;
  setWindowsField: <K extends keyof WindowsFields>(k: K, v: WindowsFields[K]) => void;
  setGateField: <K extends keyof GateFields>(k: K, v: GateFields[K]) => void;
  setGateBreakdownField: <K extends (typeof GATE_BREAKDOWN_KEYS)[number]>(
    k: K,
    v: GateFields[K],
  ) => void;
  setUnitedBreakdownField: <K extends (typeof UNITED_BREAKDOWN_KEYS)[number]>(
    k: K,
    v: WindowsFields[K],
  ) => void;
  setKpBreakdownField: <K extends (typeof KP_BREAKDOWN_KEYS)[number]>(
    k: K,
    v: WindowsFields[K],
  ) => void;
  setPodryadBreakdownField: <K extends (typeof PODRYAD_BREAKDOWN_KEYS)[number]>(
    k: K,
    v: WindowsFields[K],
  ) => void;
  setClientField: <K extends keyof ClientData>(k: K, v: ClientData[K]) => void;
  setSignature: (which: keyof Signatures, png: string) => void;
  setEmployeeSignature: (png: string) => void;
  setEmployeePodryadSignature: (png: string) => void;
  addInsert: (insert: InsertFile) => void;
  removeInsert: (index: number) => void;
  reset: () => void;
  fillTestData: (v: Variant) => void;
}

export const useWizardStore = create<WizardStoreApi>((set) => ({
  step: 'variant',
  variant: null,
  signingPersonId: 'lapaev',
  gateSigningPersonId: 'arkhiptsev',
  windows: initialWindows,
  gate: initialGate,
  client: initialClient,
  signatures: initialSignatures,
  inserts: [],

  goTo: (step) => set({ step }),
  setVariant: (variant) => set({ variant }),
  setSigningPersonId: (signingPersonId) =>
    set((s) => ({
      signingPersonId,
      signatures: signingPersonId !== 'office'
        ? { ...s.signatures, employeePodryad: null }
        : s.signatures,
    })),
  setGateSigningPersonId: (gateSigningPersonId) => set({ gateSigningPersonId }),
  setWindowsField: (k, v) =>
    set((s) => ({ windows: { ...s.windows, [k]: v } })),
  setGateField: (k, v) => set((s) => ({ gate: { ...s.gate, [k]: v } })),
  setGateBreakdownField: (k, v) =>
    set((s) => {
      const gate = { ...s.gate, [k]: v };
      return { gate: { ...gate, totalAmount: formatGateTotal(gate) } };
    }),
  setUnitedBreakdownField: (k, v) =>
    set((s) => {
      const windows = { ...s.windows, [k]: v };
      return { windows: { ...windows, totalAmount: formatUnitedTotal(windows) } };
    }),
  setKpBreakdownField: (k, v) =>
    set((s) => {
      const windows = { ...s.windows, [k]: v };
      return { windows: { ...windows, totalAmount: formatKpTotal(windows) } };
    }),
  setPodryadBreakdownField: (k, v) =>
    set((s) => ({ windows: { ...s.windows, [k]: v } })),
  setClientField: (k, v) => set((s) => ({ client: { ...s.client, [k]: v } })),
  setSignature: (which, png) =>
    set((s) => ({ signatures: { ...s.signatures, [which]: png } })),
  setEmployeeSignature: (png) =>
    set((s) => ({ signatures: { ...s.signatures, employee: png } })),
  setEmployeePodryadSignature: (png) =>
    set((s) => ({ signatures: { ...s.signatures, employeePodryad: png } })),
  addInsert: (insert) =>
    set((s) => (s.inserts.length >= MAX_INSERTS ? s : { inserts: [...s.inserts, insert] })),
  removeInsert: (index) =>
    set((s) => ({ inserts: s.inserts.filter((_, i) => i !== index) })),
  reset: () =>
    set({
      step: 'variant',
      variant: null,
      signingPersonId: 'lapaev',
      gateSigningPersonId: 'arkhiptsev',
      windows: initialWindows,
      gate: initialGate,
      client: initialClient,
      signatures: initialSignatures,
      inserts: [],
    }),

  fillTestData: (v: Variant) => {
    const today = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400_000).toISOString().slice(0, 10);

    const testClient: ClientData = {
      fullName: 'Иванов Иван Иванович',
      passportSeries: '4517',
      passportNumber: '123456',
      passportIssuedBy: 'ОUFMS России по гор. Москва по рай. Тверской',
      passportIssuedDate: '2015-06-10',
      registeredAddress: 'г. Москва, ул. Тверская, д. 5, кв. 10',
      actualAddress: '',
      phone: '+79161234567',
      phoneSecondary: '',
      email: '79997971751@ya.ru',
    };

    const testWindowsBase: WindowsFields = {
      contractNumber: '1042',
      contractDate: today,
      totalAmount: '',
      constructionsAmount: '140000',
      garbageRemovalAmount: '5000',
      slopesAmount: '25000',
      deliveryAmount: '20000',
      prepaymentAmount: '92500',
      prepaymentDueDate: tomorrow,
      paymentMethod: 'cash',
      installationAddress: 'г. Москва, ул. Ленина, д. 12, кв. 34',
      measurementDone: true,
      sizesBySeller: true,
      podryadPrepaymentMode: 'half',
      podryadPrepaymentAmount: '',
      workDemontage: '5000',
      workInstallation: '18000',
      workSlopes: '12000',
      workFlashings: '4000',
      workAdditional: '3000',
    };
    const testWindows: WindowsFields = {
      ...testWindowsBase,
      totalAmount: v === 'variant1'
        ? formatUnitedTotal(testWindowsBase)
        : formatKpTotal(testWindowsBase),
    };

    const testGateBase: GateFields = {
      contractNumber: '1042-В',
      contractDate: today,
      paymentType: '50-50',
      totalAmount: '',
      productAmount: '60000',
      preparationMaterial: '5000',
      deliveryAmount: '8000',
      installationAmount: '15000',
      preparationWork: '4000',
      demontageAmount: '3000',
      prepaymentAmount: '47500',
      prepaymentDueDate: tomorrow,
      paymentMethod: 'cash',
      installationAddress: 'г. Москва, ул. Ленина, д. 12, кв. 34',
      measurementDone: true,
      sizesBySeller: true,
    };
    const testGate: GateFields = {
      ...testGateBase,
      totalAmount: formatGateTotal(testGateBase),
    };

    if (v === 'variant3') {
      // Только ворота — окна не нужны
      set({
        step: 'yellow',
        variant: v,
        windows: initialWindows,
        gate: { ...testGate, paymentType: '50-40-10', contractNumber: '805-В' },
        client: testClient,
        signatures: initialSignatures,
        inserts: [],
      });
    } else {
      set({
        step: 'yellow',
        variant: v,
        windows: testWindows,
        gate: initialGate,
        client: testClient,
        signatures: initialSignatures,
        inserts: [],
      });
    }
  },
}));
